import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpService } from '../shared/http.service';
import { FormsService } from '../shared/forms.service';
import { AlertService } from '../shared/alert.service';

interface DepositsItem {
  consigneeDeposit: boolean;
  name: string;
  secureNumber: number;
  secureNumberConsignee: number;
  simpleDeposit: boolean;
}

interface DepositsHashItem {
  consignee: number;
  consigneeDeposit: boolean;
  simple: number;
  simpleDeposit: boolean;
}

export class Deposit {
  name = '';
  simpleDeposit = false;
  secureNumber = 0;
  consigneeDeposit = false;
  secureNumberConsignee = 0;
}

export class PromoCode {
  promoCode = '';
  discount = 0;
}

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss']
})

export class SettingsComponent implements OnInit {
  settingsForm: FormGroup;
  h1 = 'Ajustes';
  currentVersion: number;

  settingsData = {};
  settingsReady = false;

  constructor(
    private httpService: HttpService,
    private fb: FormBuilder,
    private formsService: FormsService,
    private alertService: AlertService,
    private location: Location,
  ) {
    this.createFormGroup();
  }

  ngOnInit() {
    this.loadSettings();
  }

  loadSettings() {
    this.httpService.getRaw('/Settings/CurrentSettings', [])
      .subscribe(
        response => {
          this.settingsData = response;
          this.settingsForm.patchValue(response['settings']);
          this.settingsForm.patchValue({ alEditorials: response['settings']['alEditorials'] ? response['settings']['alEditorials'].join() : '' });
          this.settingsForm.patchValue({ notifyNewRegistration: response['settings']['notifyNewRegistration'] ? response['settings']['notifyNewRegistration'].join() : '' });
          this.settingsForm.patchValue({ notifyNewConsultationClient: response['settings']['notifyNewConsultationClient'] ? response['settings']['notifyNewConsultationClient'].join() : '' });
          this.settingsForm.patchValue({ notifyNewConsultationConsignee: response['settings']['notifyNewConsultationConsignee'] ? response['settings']['notifyNewConsultationConsignee'].join() : '' });
          this.settingsForm.patchValue({ notifyNewConsultationSchool: response['settings']['notifyNewConsultationSchool'] ? response['settings']['notifyNewConsultationSchool'].join() : '' });
          this.settingsForm.patchValue({ notifyNewReservationConsignee: response['settings']['notifyNewReservationConsignee'] ? response['settings']['notifyNewReservationConsignee'].join() : '' });
          this.settingsForm.patchValue({ notifyNewReservationSchool: response['settings']['notifyNewReservationSchool'] ? response['settings']['notifyNewReservationSchool'].join() : '' });
          this.settingsForm.patchValue({ notifyNewPurchase: response['settings']['notifyNewPurchase'] ? response['settings']['notifyNewPurchase'].join() : '' });
          this.settingsForm.patchValue({ notifyNewContact: response['settings']['notifyNewContact'] ? response['settings']['notifyNewContact'].join() : '' });
          this.setDeposits(response['settings']['secureStockNumber'] ? response['settings']['secureStockNumber'] : {});
          this.setPromoCodes(response['settings']['promoCodes'] ? response['settings']['promoCodes'] : {});
          this.currentVersion = response['id'];
          this.settingsReady = true;
        }
      );
  }

  submitForm() {
    this.alertService.clearMessage();

    if (this.formsService.validateFormAndUpdateErrors(this.settingsForm)) {
      const body = this.settingsData;

      const { deposits, ...values } = this.settingsForm.value;
      body['settings'] = { ...values };
      body['settings']['alEditorials'] = this.settingsForm.get('alEditorials').value.split(',');
      body['settings']['notifyNewRegistration'] = this.settingsForm.get('notifyNewRegistration').value.split(',');
      body['settings']['notifyNewReservationConsignee'] = this.settingsForm.get('notifyNewReservationConsignee').value.split(',');
      body['settings']['notifyNewReservationSchool'] = this.settingsForm.get('notifyNewReservationSchool').value.split(',');
      body['settings']['notifyNewConsultationClient'] = this.settingsForm.get('notifyNewConsultationClient').value.split(',');
      body['settings']['notifyNewConsultationConsignee'] = this.settingsForm.get('notifyNewConsultationConsignee').value.split(',');
      body['settings']['notifyNewConsultationSchool'] = this.settingsForm.get('notifyNewConsultationSchool').value.split(',');
      body['settings']['notifyNewPurchase'] = this.settingsForm.get('notifyNewPurchase').value.split(',');
      body['settings']['notifyNewContact'] = this.settingsForm.get('notifyNewContact').value.split(',');
      body['settings']['secureStockNumber'] = this.depositsArrayToHash(this.settingsForm.get('deposits').value);
      body['settings']['promoCodes'] = this.arrayToDictio(this.settingsForm.get('promos').value, 'promoCode', ['discount']);
      body['currentVersion'] = this.currentVersion;
      this.httpService.postRaw('/Settings/', [], body)
        .subscribe(
          response => {
            this.currentVersion = response['id'];
            this.alertService.success('Se han guardado los ajustes');
          },
          _error => {
            this.alertService.error('Hubo un error al guardar los ajustes');
          }
        );
    }
  }

  /**
   *
   * @param [{name: 0001, promoCode: 2},{name: 0002, promoCode: 3}, false]
   * return: { 0001: 2, 0002: 3}
   *
   * @param [{name: 0001, {secureNumber: 2, secureNumberConsignee: 0}},{name: 0002, {secureNumber: 1, secureNumberConsignee: 1}}, true]
   * return: { 0001: {2, 0}, 0002: {1, 1}}
   *
   */
  private arrayToDictio(arr: any[], key: string, value: any) {
    const dictio = {};
    arr.map(elem => {
      if (elem[key] !== '') {
        dictio[elem[key]] = elem[value[0]];
      }
    });
    return dictio;
  }

  private depositsArrayToHash(arr: DepositsItem[]): { [key: string]: DepositsHashItem } {
    return arr.filter(({ name }) => name.trim() !== '')
    .reduce((acc, deposit) => {
      acc[deposit.name] = {
        consigneeDeposit: deposit.consigneeDeposit,
        simple: deposit.secureNumber,
        consignee: deposit.secureNumberConsignee,
        simpleDeposit: deposit.simpleDeposit,
      };

      return acc;
    }, {});
  }

  private setDeposits(deposits: { [key: string]: { simple: number; consignee: number }}) {
    const depositFormGroup: FormGroup[] = Object.keys(deposits).map(name => this.fb.group({
      name,
      simpleDeposit: deposits[name]['simpleDeposit'] || false,
      secureNumber: deposits[name]['simple'],
      consigneeDeposit: deposits[name]['consigneeDeposit'] || false,
      secureNumberConsignee: deposits[name]['consignee'],
    }));

    const depositsFormArray = this.fb.array(depositFormGroup);
    this.settingsForm.setControl('deposits', depositsFormArray);
  }

  addDeposit() {
    this.deposits.push(this.fb.group(new Deposit()));
  }

  deleteDeposit(index: number) {
    if (confirm('Eliminar depósito?')) {
      (<FormArray> this.settingsForm.get('deposits')).removeAt(index);
    }
  }

  get deposits(): FormArray {
    return this.settingsForm.get('deposits') as FormArray;
  }

  private setPromoCodes(promos) {
    const arrPromoCodes = [];
    for (const promoCode in promos) {
      if (promos.hasOwnProperty(promoCode)) {
        arrPromoCodes.push({promoCode: promoCode, discount: promos[promoCode]});
      }
    }
    const promoCodesFormGroup = arrPromoCodes.map(promos => this.fb.group(promos));
    const promoCodesFormArray = this.fb.array(promoCodesFormGroup);
    this.settingsForm.setControl('promos', promoCodesFormArray);
  }

  addPromoCode() {
    this.promos.push(this.fb.group(new PromoCode()));
  }

  deletePromoCode(index) {
    if (confirm('Eliminar Promo Code?')) {
      (<FormArray> this.settingsForm.get('promos')).removeAt(index);
    }
  }

  get promos(): FormArray {
    return this.settingsForm.get('promos') as FormArray;
  }

  goBack() {
    this.location.back();
  }

  private createFormGroup() {
    this.settingsForm = this.fb.group({
      deposits: this.fb.array([]),
      promos: this.fb.array([]),
      tax: [
        null,
        Validators.compose([
          Validators.required,
          Validators.pattern(/^[0-9]{1,3}\.?[0-9]{0,3}$/)
        ])
      ],
      shippingPriceMontevideo: [
        null,
        Validators.compose([
          Validators.required,
          Validators.pattern(/^[0-9]{1,3}\.?[0-9]{0,3}$/)
        ])
      ],
      shippingPriceInterior: [
        null,
        Validators.compose([
          Validators.required,
          Validators.pattern(/^[0-9]{1,3}\.?[0-9]{0,3}$/)
        ])
      ],
      freeShippingThreshold: [
        null,
        Validators.compose([
          Validators.required,
          Validators.pattern(/^[0-9]{1,4}\.?[0-9]{0,3}$/)
        ])
      ],
      activeThreshold: [
        false,
        null
      ],
      alEditorials: [
        null,
        Validators.compose([
          Validators.required
        ])
      ],
      notifyNewRegistration: [
        null,
        Validators.compose([
          Validators.required
        ])
      ],
      notifyNewConsultationClient: [
        null,
        Validators.compose([
          Validators.required
        ])
      ],
      notifyNewConsultationConsignee: [
        null,
        Validators.compose([
          Validators.required
        ])
      ],
      notifyNewConsultationSchool: [
        null,
        Validators.compose([
          Validators.required
        ])
      ],
      notifyNewPurchase: [
        null,
        Validators.compose([
          Validators.required
        ])
      ],
      notifyNewReservationConsignee: [
        null,
        Validators.compose([
          Validators.required
        ])
      ],
      notifyNewReservationSchool: [
        null,
        Validators.compose([
          Validators.required
        ])
      ],
      notifyNewContact: [
        null,
        Validators.compose([
          Validators.required
        ])
      ]
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/settings/settings.component.ts